/**
 * supportActions.ts — de reparaties die support daadwerkelijk kan doen.
 *
 * HET GAT DAT DIT DICHT
 *
 * Break glass ging open en daarachter zat alleen INZAGE. Kreeg support een
 * supportnummer van een school ("Klaasje kan niet inloggen", "er kijkt een
 * vreemde mee"), dan was het enige beschikbare middel: de voortgang wissen. Dat
 * is het grofste instrument dat er is en het lost geen van beide meldingen op.
 * Een supportkanaal dat niets kan, wordt op termijn omzeild in plaats van
 * gerespecteerd — en dat omzeilen is precies wat we met de pseudonimisering
 * wilden voorkomen.
 *
 * KIJKEN VERSUS DOEN
 *
 * Deze vier acties hebben GEEN break-glass-venster nodig, en dat is een besluit
 * en geen omissie. Een venster verantwoordt inzage in de gegevens van een kind;
 * een beheerder die een onterechte koppeling verbreekt hoeft die gegevens niet
 * te zien. Zaten alle acties achter het venster, dan stond het venster
 * permanent open — en dan zegt een open venster niets meer.
 *
 * De prijs van die uitzondering: elke actie eist een REDEN van minstens tien
 * tekens en landt in `admin_actions`. Zonder dat zou "geen venster nodig"
 * hetzelfde betekenen als "onopgemerkt".
 *
 * Zie `supabase/migrations/0042_support_actions.sql`.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
export interface SupportActionResult {
  ok: boolean;
  message?: string;
  /**
   * Hoeveel rijen de ingreep raakte. NUL IS GEEN FOUT MAAR OOK GEEN SUCCES:
   * dan was er niets om te repareren, en dat moet het scherm zeggen. Anders
   * meldt support aan een school dat het is opgelost terwijl er niets gebeurde.
   */
  affected?: number;
  /** Bij een nieuwe koppelcode: de code zoals de server hem nu kent. */
  code?: string;
}

/**
 * Verbreekt een ouderkoppeling die er niet had moeten zijn.
 *
 * Vandaag kan alleen de onterecht gekoppelde persoon zichzelf ontkoppelen — wat
 * precies degene is die dat niet gaat doen — of het kind zelf. Een school die
 * belt, kon niets.
 *
 * Dit wist de RELATIE, niet het account van de ouder en niet de gegevens van het
 * kind.
 */
export async function unlinkParent(learnerId: string, parentId: string, reason: string): Promise<SupportActionResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_unlink_parent', {
    p_learner_id: learnerId,
    p_parent_id: parentId,
    p_reason: reason
  });
  if (error) {
    logError('[SupportActions] Ontkoppelen mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, 'De koppeling is niet verbroken. Er is niets gewijzigd.')
    };
  }
  return {
    ok: true,
    affected: Number(data ?? 0)
  };
}

/**
 * Geeft een verse koppelcode uit die de OUDER zelf verzilvert.
 *
 * Bewust geen rechtstreekse koppeling: een beheerder die dat kan, omzeilt de
 * hele koppelcode-architectuur via support. Dan geldt de zorgvuldigheid rond die
 * code (hashen, eenmalig, ratelimiet) alleen voor wie geen support belt.
 *
 * Let op: een verse code betekent dat de oude dood is. Dat raakt iedereen die de
 * oude nog had — het scherm zegt dat erbij.
 */
export async function issueLinkCode(learnerId: string, reason: string): Promise<SupportActionResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_issue_link_code', {
    p_learner_id: learnerId,
    p_reason: reason
  });
  if (error) {
    logError('[SupportActions] Koppelcode uitgeven mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, 'Er is geen nieuwe code uitgegeven. De oude code werkt nog.')
    };
  }
  return {
    ok: true,
    code: String(data)
  };
}

/**
 * Maakt een herstelcode ongeldig.
 *
 * Een herstelcode geeft een wachtwoordreset en bewijst dus IDENTITEIT, niet een
 * relatie. Dat maakt hem het zwaarste artefact in het systeem, en het enige
 * waarvoor "op straat komen te liggen" onmiddellijk ingrijpen vraagt.
 *
 * Er komt geen nieuwe code in de plaats: een beheerder die een herstelcode
 * aanmaakt, heeft die code even in handen. De gebruiker maakt zelf een nieuwe in
 * zijn instellingen.
 */
export async function revokeRecoveryCode(subjectId: string, reason: string): Promise<SupportActionResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_revoke_recovery_code', {
    p_subject_id: subjectId,
    p_reason: reason
  });
  if (error) {
    logError('[SupportActions] Herstelcode intrekken mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, 'De herstelcode is niet ingetrokken.')
    };
  }
  // De RPC geeft `false` als er niets was om in te trekken. Dat is een ander
  // antwoord dan "gelukt", en het scherm zegt het verschil.
  return {
    ok: true,
    affected: data === true ? 1 : 0
  };
}

/**
 * Haalt een kind uit alle klassen.
 *
 * De enige route waarlangs schoolinzage stopt zonder dat de oude leerkracht
 * meewerkt — en dat is precies het geval waarin het nodig is (kind gewisseld van
 * school, oude leerkracht kijkt nog mee).
 *
 * Bewust geen "verplaats naar school X": een beheerder kan niet vaststellen dat
 * dit kind op die nieuwe school zit. De nieuwe leerkracht koppelt het kind zelf
 * met de klascode; dat is de partij die het antwoord kent.
 */
export async function detachLearnerFromClasses(learnerId: string, reason: string): Promise<SupportActionResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_detach_learner_from_classes', {
    p_learner_id: learnerId,
    p_reason: reason
  });
  if (error) {
    logError('[SupportActions] Losmaken van klassen mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, 'Het kind is niet losgemaakt. Er is niets gewijzigd.')
    };
  }
  return {
    ok: true,
    affected: Number(data ?? 0)
  };
}
export interface AdminActionRecord {
  id: string;
  subjectId: string | null;
  action: string;
  reason: string;
  createdAt: string;
}

/**
 * Het eigen logboek van ingrepen.
 *
 * Alleen de EIGEN regels — die reden is vrije tekst over een kind, en het
 * logboek dat zorgvuldigheid moet aantonen mag zelf geen bron van gevoelige
 * gegevens worden. Dat was in 0033 een echt gat.
 */
export async function listOwnActions(subjectId?: string): Promise<{
  ok: boolean;
  actions: AdminActionRecord[];
  message?: string;
}> {
  const {
    data,
    error
  } = await supabase.rpc('admin_list_actions', {
    p_subject_id: subjectId ?? null
  });
  if (error) {
    logError('[SupportActions] Logboek ophalen mislukt', error);
    return {
      ok: false,
      actions: [],
      message: 'Het logboek kon niet worden opgehaald. Dit is geen leeg logboek.'
    };
  }
  return {
    ok: true,
    actions: (data ?? []).map((r: Record<string, unknown>) => ({
      id: String(r.id),
      subjectId: (r.subject_id as string | null) ?? null,
      action: String(r.action),
      reason: String(r.reason),
      createdAt: String(r.created_at)
    }))
  };
}

/**
 * Vertaalt een databasefout naar iets bruikbaars.
 *
 * De enige fout die een beheerder zelf kan oplossen is een te korte reden. Al
 * het andere is een storing, en dan is de ruwe Postgres-melding voor niemand
 * nuttig: die noemt interne functienamen en zegt niets over wat er te doen valt.
 */
function describeError(raw: string | undefined, fallback: string): string {
  if (raw?.includes('10 tekens')) {
    return 'Geef een uitgebreidere reden. Die reden is wat deze ingreep verantwoordt, dus één woord is niet genoeg.';
  }
  if (raw?.includes('beheerder')) {
    return 'Je hebt geen beheerdersrechten voor deze handeling.';
  }
  return fallback;
}
